import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { pageCountOf, useClampPage, usePageParam } from '@shared/pagination';
import { formatPhoneForReading } from '@shared/format';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { ListSearch } from '@shared/ui/ListSearch';
import { BanIcon, UnbanIcon, UserOffIcon } from '@shared/ui/icons';
import { DocumentIcon } from '@shared/ui/icons/nav';
import { Pager } from '@shared/ui/pager';
import { RowMenu } from '@shared/ui/rowmenu';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { AccountStateBadge, Avatar, RoleBadges, SurfaceError } from '../_shared/components';
import { roleLabel, sharedCopy } from '../_shared/copy';
import { useBlockUser, useUnblockUser } from '../_shared/mutations';
import { ROLE_FILTER_VALUES } from '../_shared/roles';
import type { AdminUserRow } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { adminUsersKey, useAdminUsers } from './queries';

const TABLE_COLUMNS = 5;

/** The facet's «no filter» member. It is a UI value and never reaches the wire. */
const ALL = 'all';

/**
 * `/users` — the account register.
 *
 * **One filter, and the register carries no second one.** The catalog row names
 * the role facet by itself: there is no search field over accounts (search on
 * this platform is over title and description — the catalogue, not the roster),
 * no state facet, no bulk-selection column and no join-date column. Each of
 * those absences is a row that does not exist rather than a decision taken here.
 *
 * **The pager is not one of them.** `backend-auth` pages this list by offset and
 * the console pinned it to page one, which left every account past the first
 * page unreachable — and the roster is the route to a block, a role grant, and
 * an account's case history, so an unreachable row is an account nobody can act
 * on. The page rides the URL beside the facet: an operator working an account
 * hands the address to a colleague, and a page number is theirs to share where a
 * keyset cursor would not be.
 *
 * **Changing the facet returns to page one.** Page four of «كلّ الأدوار» is not
 * page four of «مشغّل», and carrying the ordinal across would answer a question
 * nobody asked with rows from a list that no longer exists.
 *
 * **The filter is a form, not a chip strip.** The roles are mutually exclusive,
 * a chip strip announces a multi-select this filter is not, and changing a
 * `<select>` never submits: «طبّق» applies and «امسح المرشّح» clears and returns
 * focus to the field, because the copy of that control inside the empty block
 * leaves the page the moment it succeeds.
 *
 * **The filter is a server read, not a client sweep.** The pack hides rows with
 * the `hidden` attribute because a static file cannot re-query; production sends
 * the facet and renders what came back, so a filtered view is the whole answer
 * rather than one page of it with rows removed.
 *
 * **The destructive action is quiet, loud, and reversible in that order** — a
 * menu entry rather than a button in the row, a dialog that names the person,
 * and copy that says the block takes nothing away permanently. Unblock takes the
 * same menu and no dialog: a confirmation stands in front of what a press takes
 * away, and restoring access takes nothing.
 */
export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const roleFieldRef = useRef<HTMLSelectElement>(null);

  const activeRole = searchParams.get('role');
  const term = searchParams.get('q') ?? '';
  const { page: activePage, goToPage } = usePageParam();
  const query = useAdminUsers(activeRole, activePage, term);

  // Held in the URL and clamped to a page that exists — `?page=5` on a
  // three-page list used to render «صفحة ٥ من ٣» with both traversal buttons
  // live, which is a location that does not exist presented as a valid one.
  const totalPages = query.data ? pageCountOf(query.data.total, query.data.page_size) : undefined;
  useClampPage(activePage, totalPages);

  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();

  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);
  const [pendingBlock, setPendingBlock] = useState<AdminUserRow | null>(null);

  const rows = query.data?.items ?? [];

  function applyFilter(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get('role');
    const next = new URLSearchParams(searchParams);

    if (typeof value === 'string' && value !== ALL) next.set('role', value);
    else next.delete('role');

    // A new facet is a new list, so the ordinal from the old one does not ride
    // across it.
    next.delete('page');
    setSearchParams(next);
  }

  /**
   * The term rides in the URL beside the role, written on `replace`: typing is
   * one continuous act, and a history entry per keystroke turns Back into a
   * character-by-character undo of the search box. The role facet keeps its
   * `push`, because pressing «طبّق» is a step a reader expects Back to undo.
   */
  function setTerm(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function clearFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete('role');
    next.delete('q');
    next.delete('page');
    setSearchParams(next);
    // The control that cleared the filter may be the one inside the empty block,
    // and that block leaves the page the moment the clear succeeds. Focus lands
    // on the field rather than on `<body>`.
    roleFieldRef.current?.focus();
  }

  function askBlock(user: AdminUserRow) {
    setPendingBlock(user);
    setConfirmSubject({
      heading: sharedCopy.block.heading(user.display_name),
      body: sharedCopy.block.body,
      commitLabel: sharedCopy.block.commit,
      // Reversible-but-consequential. The pack draws the commit button in the
      // danger colour; the repo's confirmation posture assigns a block to
      // `warning`, because the dialog's intent states whether the effect can be
      // undone and this one can — the account signs in again the moment the
      // block is lifted.
      intent: 'warning',
    });
  }

  function onWriteFailure(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
  }

  function commitBlock() {
    const user = pendingBlock;
    if (!user) return;

    blockUser.mutate(user.id, {
      onSuccess: () => {
        setConfirmSubject(null);
        setPendingBlock(null);
        toast.show(sharedCopy.block.blocked, 'success');
      },
      onError: (error) => {
        setConfirmSubject(null);
        setPendingBlock(null);
        onWriteFailure(error);
      },
    });
  }

  function unblock(user: AdminUserRow) {
    unblockUser.mutate(user.id, {
      onSuccess: () => toast.show(sharedCopy.block.unblocked, 'success'),
      onError: onWriteFailure,
    });
  }

  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <section aria-labelledby="users-heading">
        <h2 id="users-heading" className="mb-4 font-display text-h2 font-bold">
          {copy.section.heading}
        </h2>

        <ListSearch
          label={copy.search.label}
          placeholder={copy.search.placeholder}
          hint={copy.search.hint}
          value={term}
          onChange={setTerm}
        />

        <form onSubmit={applyFilter} className="mb-4 flex flex-wrap items-end gap-3">
          <p id="users-filter-legend" className="sr-only">
            {copy.filter.legend}
          </p>

          <div className="grid gap-2">
            <label htmlFor="users-role-filter" className="text-sm font-medium text-ink">
              {copy.filter.label}
            </label>
            <select
              id="users-role-filter"
              ref={roleFieldRef}
              name="role"
              defaultValue={activeRole ?? ALL}
              // A fresh mount after «امسح المرشّح» has to show the cleared value,
              // so the field is keyed on what the URL currently holds.
              key={activeRole ?? ALL}
              aria-describedby="users-filter-legend"
              className="hit-target min-w-[14rem] rounded-sm border border-border-strong bg-surface-raised px-3 text-sm text-ink"
            >
              <option value={ALL}>{copy.filter.all}</option>
              {ROLE_FILTER_VALUES.map((role) => (
                <option key={role} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
          >
            {copy.filter.apply}
          </button>

          <button
            type="button"
            onClick={clearFilter}
            className="hit-target rounded-sm px-4 text-sm text-ink-muted hover:bg-hover-bg hover:text-ink"
          >
            {copy.filter.reset}
          </button>
        </form>

        {query.isError ? (
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() =>
              void queryClient.invalidateQueries({ queryKey: adminUsersKey(activeRole, activePage) })
            }
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <UserTable>
              <RowSkeleton columns={TABLE_COLUMNS} />
            </UserTable>
          </>
        ) : null}

        {query.data ? (
          rows.length === 0 ? (
            <EmptyState
              message={
                activePage > 1
                  ? copy.states.emptyPage
                  : term.trim()
                    ? copy.states.emptySearched(term)
                    : activeRole
                      ? copy.states.emptyFiltered
                      : copy.states.empty
              }
              action={
                activeRole || term.trim() ? (
                  <button
                    type="button"
                    onClick={clearFilter}
                    className="hit-target flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
                  >
                    <UserOffIcon />
                    {copy.states.emptyFilteredAction}
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <UserTable>
                {rows.map((user) => (
                  <tr key={user.id}>
                    <DTableRowHeader>
                      <span className="flex items-center gap-3">
                        <Avatar />
                        <span className="grid">
                          <span>{user.display_name}</span>
                          {/* The handle is an identifier: Latin, mono, isolated. */}
                          <span className="mono mt-px text-xs text-ink-muted">
                            <bdi>@{user.handle}</bdi>
                          </span>
                        </span>
                      </span>
                    </DTableRowHeader>

                    <td className="whitespace-nowrap">
                      {user.phone_e164 ? (
                        // Stored and sent as issued — ASCII E.164 — and read in
                        // the operator's own digits, national form, isolated.
                        <bdi>{formatPhoneForReading(user.phone_e164)}</bdi>
                      ) : (
                        <span className="text-xs text-ink-muted">{copy.phoneUnavailable}</span>
                      )}
                    </td>

                    <td>
                      <RoleBadges role={user.role} />
                    </td>

                    <td>
                      <AccountStateBadge blocked={user.is_blocked} />
                    </td>

                    <td className="w-[3.5rem]">
                      <RowMenu
                        label={copy.rowMenu.label(user.display_name)}
                        entries={[
                          {
                            key: 'open',
                            label: copy.rowMenu.open,
                            icon: <DocumentIcon />,
                            onSelect: () => void navigate(`/users/${user.id}`),
                          },
                          user.is_blocked
                            ? {
                                key: 'unblock',
                                label: sharedCopy.block.unblockMenu,
                                icon: <UnbanIcon />,
                                onSelect: () => unblock(user),
                              }
                            : {
                                key: 'block',
                                label: sharedCopy.block.menu,
                                icon: <BanIcon />,
                                danger: true,
                                onSelect: () => askBlock(user),
                              },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </UserTable>
            </>
          )
        ) : null}

        {/*
          Outside the row check on purpose: a page walked one past the end has no
          rows and the control that walks back is the only way off it.
        */}
        {query.data ? (
          <Pager
            label={copy.pager.label}
            page={activePage}
            pageCount={totalPages}
            shown={rows.length}
            total={query.data.total}
            noun={{
              one: copy.pager.shownOne,
              two: copy.pager.shownTwo,
              few: copy.pager.shownFew,
              many: copy.pager.shownMany,
            }}
            hasPrevious={activePage > 1}
            hasNext={query.data.has_next}
            onPrevious={() => goToPage(activePage - 1)}
            onNext={() => goToPage(activePage + 1)}
            onGoToPage={goToPage}
          />
        ) : null}
      </section>

      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={blockUser.isPending}
        onConfirm={commitBlock}
        onCancel={() => {
          setConfirmSubject(null);
          setPendingBlock(null);
        }}
      />
    </>
  );
}

/**
 * The table frame, shared by the skeleton and the data states so neither
 * reflows the other. Five columns and a `40rem` floor: the first cell is a
 * two-line identity with a face beside it and the second is a number that must
 * not wrap, and the register's own measurement is what sets the width.
 */
function UserTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.caption} minInlineSize="40rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.user}</th>
            <th scope="col">{copy.columns.phone}</th>
            <th scope="col">{copy.columns.roles}</th>
            <th scope="col">{copy.columns.state}</th>
            <th scope="col">
              <span className="sr-only">{copy.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}
